import { StyledRow, WrapLgMarginBottom } from "App/global-style";
import {
  CoverageNeed,
  NeedName,
  ProductCoverageName,
  ProductName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  getPaymentRestriction,
  isNotAllowed,
} from "Store/akaAdvice/interfaces/getters";

import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { Checkbox } from "Components/Form/Checkbox";
import { Select } from "Components/Form/Select";
import { VALUE_TYPE } from "Components/NumberValue";
import { getPriceFormatFromValue } from "Formatting/index";
import { UserAuthorizationLevel } from "Store/advice/interfaces";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { useAkaAdviceBaseAmountSelector } from "Store/akaAdvice/akaAdviceSelectors";
import { getCorrectAkaAdviceValues } from "Store/akaAdvice/mappers/mapToFormValues/utils";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { AkaGroupLifeCoverage } from "Store/interfaces/products/coverage";
import { TextLanguage } from "Store/texts/interfaces";
import { getError } from "Utils/productUtils";
import { FormikProps } from "formik";
import React from "react";
import { useAdviceIsLoading } from "../../../hooks/stateSelectors";
import { InsurancePrice } from "../../AdvicePrice/InsurancePrice";
import { AkaAdviceProductCoverages } from "../../AkaAdviceProductCoverage/AkaAdviceProductCoverages";
import { CloseToMaxAgeWarning } from "../../CloseToMaxAgeWarning";
import { AkaAdvicePriceFormValues } from "../../interfaces";
import { MarginLeftWrapper } from "../../styled";
import { getAkaGLevelOptions } from "../../utils";
import {
  AKA_GROUP_LIFE_DISABILITY_INTEREST_END_AGE,
  AKA_GROUP_LIFE_DISABILITY_INTEREST_MAX_AGE,
} from "../disabilityAdviceCardConstants";

interface Props {
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  need: CoverageNeed;
  recalculatePrice: (
    formValues: AkaAdvicePriceFormValues,
    specificProduct: ProductName,
  ) => void;
  priceIsChanging: boolean;
  priceIsLoading: boolean;
  adviceCard: any;
  lang: TextLanguage;
}

export const AkaGroupLifeTimeLimitedDisabilityInterestForm = ({
  formProps,
  need,
  recalculatePrice,
  priceIsChanging,
  priceIsLoading,
  adviceCard,
  lang,
}: Props): React.ReactElement | null => {
  const coverageName =
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST;
  const isLoading = useAdviceIsLoading();
  const isAdvisor = useIsAdvisorSelector();
  const baseAmount = useAkaAdviceBaseAmountSelector();
  const maxPayout = getPaymentRestriction(need, coverageName).maxPayouts[
    isAdvisor
      ? UserAuthorizationLevel.PRODUCT_MANAGER
      : UserAuthorizationLevel.CUSTOMER
  ];
  const adviceValueAkaGroupLifeTime = getCorrectAkaAdviceValues(
    need,
    coverageName,
    isAdvisor,
  ).adviceValue;

  if (isNotAllowed(need, coverageName)) {
    return null;
  }

  return (
    <WrapLgMarginBottom>
      <StyledRow className="AkaAdviceInsuranceForm">
        <div>
          <Checkbox
            id="timeLimitedDisabilityInterestCheckbox"
            name="timeLimitedDisabilityInterestCheckbox"
            label="A+ Gruppeliv tidsbegrenset uførerente"
            hint={
              adviceValueAkaGroupLifeTime > 0
                ? `En tidsbegrenset uførerente kompenserer for inntektene du mister etter sykepengene opphører og frem til du defineres som varig ufør. Vi anbefaler en utbetaling på ${getPriceFormatFromValue(
                    adviceValueAkaGroupLifeTime * baseAmount,
                    VALUE_TYPE.NOK,
                  )} (${adviceValueAkaGroupLifeTime} G)`
                : undefined
            }
          />
          {formProps.values.timeLimitedDisabilityInterestCheckbox && (
            <MarginLeftWrapper>
              <Select
                large
                id="timeLimitedDisabilityInterestGLevel"
                name="timeLimitedDisabilityInterestGLevel"
                placeholder="Velg dekning"
                options={getAkaGLevelOptions(
                  "timeLimitedDisabilityInterestGLevel",
                  maxPayout,
                  baseAmount,
                )}
                disabled={isLoading}
                error={getError(
                  "timeLimitedDisabilityInterestGLevel",
                  formProps,
                )}
                onChange={(e) =>
                  recalculatePrice(
                    {
                      ...formProps.values,
                      timeLimitedDisabilityInterestGLevel: e.target.value,
                    },
                    INSURANCE_NAME_VALUE.AKA_GROUP_LIFE,
                  )
                }
              />
            </MarginLeftWrapper>
          )}
        </div>
        <InsurancePrice
          needName={NeedName.DISABILITY}
          productCoverages={[coverageName]}
          priceIsChanging={priceIsChanging}
          priceIsLoading={priceIsLoading}
        />
      </StyledRow>
      <CloseToMaxAgeWarning
        need={need}
        coverageName={coverageName}
        warningTexts={adviceCard.warnings}
        lang={lang}
        warningVariables={{
          maxAge: AKA_GROUP_LIFE_DISABILITY_INTEREST_MAX_AGE,
          endAge: AKA_GROUP_LIFE_DISABILITY_INTEREST_END_AGE,
        }}
      />
      <AkaAdviceProductCoverages
        backendInsuranceName={ProductBackendName.AKA_GROUP_LIFE}
        coverageGroup={AkaGroupLifeCoverage.AKA_GROUP_LIFE_INCAP_AND_TIME}
      />
    </WrapLgMarginBottom>
  );
};
